import {
  appendBezierCurve,
  clip,
  endPath,
  lineTo,
  moveTo,
  PDFArray,
  PDFDocument,
  PDFFont,
  PDFImage,
  PDFName,
  PDFPage,
  PDFString,
  popGraphicsState,
  pushGraphicsState,
  rgb,
  StandardFonts,
} from 'pdf-lib';
import { patchFontWidth, patchPageDrawText } from '../safeText';
import type {
  BookingPdfAccommodation,
  BookingPdfData,
  BookingPdfFlight,
  BookingPdfInstructionSection,
  BookingPdfPrice,
  BookingPdfPriceLine,
  BookingPdfSegment,
  Rgb,
} from './types';

// Booking confirmation PDF — built with pdf-lib.
// Visual reference is the confirmation email at
// apps/backend/src/user-templates/templates/BookingConfirmation.tsx. The PDF
// re-implements the same sections (header, traveler, flight cards with
// stopovers, loyalty, accommodation cards with photo/instructions/fees, price
// summary, footer) using pdf-lib primitives.

// ---- Colors --------------------------------------------------------------
const C = {
  ink: [0.067, 0.094, 0.153] as Rgb, // gray-900
  ink2: [0.122, 0.161, 0.216] as Rgb, // gray-800
  muted: [0.42, 0.447, 0.502] as Rgb, // gray-500
  muted2: [0.612, 0.639, 0.686] as Rgb, // gray-400
  rule: [0.898, 0.91, 0.922] as Rgb, // gray-200
  ruleSoft: [0.933, 0.941, 0.953] as Rgb, // gray-100
  good: [0.075, 0.451, 0.2] as Rgb, // #137333
  bad: [0.702, 0.149, 0.118] as Rgb, // #b3261e
  white: [1, 1, 1] as Rgb,
  hero: [0.933, 0.945, 0.961] as Rgb,
  neutralBadge: [0.4, 0.447, 0.522] as Rgb,
} satisfies Record<string, Rgb>;

// ---- Internals -----------------------------------------------------------

type Fonts = {
  regular: PDFFont;
  bold: PDFFont;
};

type CardState = {
  /** Y at which the current card portion starts on its page (top of the box) */
  topY: number;
  /** Page the current card portion lives on — we draw the border on this page,
   * not on `ctx.page`, because pagination inside the card moves the cursor on. */
  page: PDFPage;
  /** False on the page that opened the card, true on every continuation page. */
  isContinuation: boolean;
};

type Ctx = {
  doc: PDFDocument;
  page: PDFPage;
  fonts: Fonts;
  pageW: number;
  pageH: number;
  margin: number;
  contentW: number;
  x: number;
  y: number;
  /** Active card whose border is awaiting a closer. Set by beginCard, cleared
   * by endCard; newPage uses it to draw partial borders on each page the card
   * spans so a paginated card visually carries across the break. */
  card?: CardState;
};

function rgbColor(channels: Rgb) {
  return rgb(channels[0], channels[1], channels[2]);
}

function makeCtx(doc: PDFDocument, page: PDFPage, fonts: Fonts): Ctx {
  const pageW = page.getWidth();
  const pageH = page.getHeight();
  const margin = 48;
  return {
    doc,
    page,
    fonts,
    pageW,
    pageH,
    margin,
    contentW: pageW - margin * 2,
    x: margin,
    y: pageH - margin,
  };
}

// Word-wrap a string into lines that fit within `maxWidth` at the given font/size.
function wrapText(text: string, font: PDFFont, size: number, maxWidth: number): string[] {
  const paragraphs = String(text).split('\n');
  const out: string[] = [];
  for (const para of paragraphs) {
    const words = para.split(/\s+/);
    let line = '';
    for (const word of words) {
      const trial = line ? `${line} ${word}` : word;
      if (font.widthOfTextAtSize(trial, size) <= maxWidth) {
        line = trial;
      } else {
        if (line) {
          out.push(line);
        }
        line = word;
      }
    }
    if (line) {
      out.push(line);
    }
  }
  return out;
}

// ---- Primitives ----------------------------------------------------------

function drawHeader(ctx: Ctx, data: BookingPdfData) {
  ctx.page.drawText(data.title, {
    x: ctx.margin,
    y: ctx.y - 18,
    font: ctx.fonts.bold,
    size: 20,
    color: rgbColor(C.ink),
  });
  ctx.y -= 28;

  const sub = `Departing ${data.tripDate ?? ''}   ·   Booking ID ${data.bookingId ?? ''}`;
  ctx.page.drawText(sub, {
    x: ctx.margin,
    y: ctx.y - 10,
    font: ctx.fonts.regular,
    size: 10,
    color: rgbColor(C.muted),
  });
  ctx.y -= 22;
}

function drawRule(ctx: Ctx, gap = 32) {
  ctx.y -= gap / 2;
  ctx.page.drawLine({
    start: { x: ctx.margin, y: ctx.y },
    end: { x: ctx.margin + ctx.contentW, y: ctx.y },
    thickness: 0.6,
    color: rgbColor(C.rule),
  });
  ctx.y -= gap / 2;
}

function drawSectionTitle(ctx: Ctx, label: string) {
  ctx.page.drawText(label, {
    x: ctx.margin,
    y: ctx.y - 11,
    font: ctx.fonts.bold,
    size: 12,
    color: rgbColor(C.ink),
  });
  ctx.y -= 18;
}

function drawKV(ctx: Ctx, label: string, value: string, opts: { x?: number; labelWidth?: number } = {}) {
  const size = 10;
  const x = opts.x ?? ctx.margin;
  const labelWidth = opts.labelWidth ?? 110;
  ctx.page.drawText(label, {
    x,
    y: ctx.y - size,
    font: ctx.fonts.regular,
    size,
    color: rgbColor(C.muted),
  });
  ctx.page.drawText(String(value), {
    x: x + labelWidth,
    y: ctx.y - size,
    font: ctx.fonts.regular,
    size,
    color: rgbColor(C.ink2),
  });
  ctx.y -= 14;
}

// Returns a closer that finishes the rectangle around the inner card content
// once `ctx.y` reflects the bottom of the content.
// Pixel radius for rounded card corners. Kept small — the design language
// stays mostly orthogonal, the rounding just softens the rectangle slightly.
const CARD_CORNER_RADIUS = 6;

// Draw any subset of the four sides of a rounded rectangle. Used by the card
// border system: a single-page card gets all four sides (closed rounded rect),
// the originating page of a paginated card gets top + sides (rounded top
// corners, open at the bottom), continuation pages get sides + bottom (rounded
// bottom corners, open at the top). We render the whole thing as one SVG path
// so the bezier corners and straight runs line up cleanly.
function drawCardEdges(
  page: PDFPage,
  rect: { x: number; y: number; width: number; height: number },
  sides: { top: boolean; bottom: boolean },
) {
  const { x, y, width: w, height: h } = rect;
  const r = CARD_CORNER_RADIUS;
  let path: string;
  if (sides.top && sides.bottom) {
    // Full closed rounded rect — drawn clockwise from top-left.
    path = `M ${r} 0 H ${w - r} Q ${w} 0 ${w} ${r} V ${h - r} Q ${w} ${h} ${w - r} ${h} H ${r} Q 0 ${h} 0 ${h - r} V ${r} Q 0 0 ${r} 0 Z`;
  } else if (sides.top) {
    // Originating page: bottom-left up, top with rounded corners, down to bottom-right. No bottom edge.
    path = `M 0 ${h} V ${r} Q 0 0 ${r} 0 H ${w - r} Q ${w} 0 ${w} ${r} V ${h}`;
  } else if (sides.bottom) {
    // Continuation page: top-left down, bottom with rounded corners, up to top-right. No top edge.
    path = `M 0 0 V ${h - r} Q 0 ${h} ${r} ${h} H ${w - r} Q ${w} ${h} ${w} ${h - r} V 0`;
  } else {
    // Rare middle page of a 3+ page card — sides only, no rounded corners visible.
    path = `M 0 0 V ${h} M ${w} 0 V ${h}`;
  }
  // SVG path coords are anchored at the top-left of the rect in PDF space; the
  // SVG y-axis grows downward so `h` lands at the rect's PDF bottom.
  page.drawSvgPath(path, {
    x,
    y: y + h,
    borderColor: rgbColor(C.rule),
    borderWidth: 0.6,
  });
}

function beginCard(ctx: Ctx, padding = 14): () => void {
  const previousCard = ctx.card;
  ctx.card = { topY: ctx.y, page: ctx.page, isContinuation: false };
  ctx.y -= padding;
  const previousX = ctx.x;
  const previousContentW = ctx.contentW;
  ctx.x = ctx.margin + padding;
  ctx.contentW = ctx.pageW - ctx.margin * 2 - padding * 2;
  return () => {
    if (ctx.card) {
      const bottom = ctx.y - padding;
      drawCardEdges(
        ctx.card.page,
        { x: ctx.margin, y: bottom, width: ctx.pageW - ctx.margin * 2, height: ctx.card.topY - bottom },
        { top: !ctx.card.isContinuation, bottom: true },
      );
      ctx.y = bottom;
    }
    ctx.card = previousCard;
    ctx.x = previousX;
    ctx.contentW = previousContentW;
  };
}

function drawAirlineBadge(ctx: Ctx, x: number, y: number, w: number, h: number, code: string, bg: Rgb) {
  ctx.page.drawRectangle({ x, y, width: w, height: h, color: rgbColor(bg) });
  const tw = ctx.fonts.bold.widthOfTextAtSize(code, 8);
  ctx.page.drawText(code, {
    x: x + (w - tw) / 2,
    y: y + (h - 8) / 2 + 1,
    font: ctx.fonts.bold,
    size: 8,
    color: rgbColor(C.white),
  });
}

// ---- Sections ------------------------------------------------------------

function drawTraveler(ctx: Ctx, data: BookingPdfData) {
  drawSectionTitle(ctx, 'Traveler');
  drawKV(ctx, 'Name', data.traveler.name);
  drawKV(ctx, 'Email', data.traveler.email);
}

// Render a single segment row inside a flight card — airline strip then a
// three-column endpoint block (date/time/city/terminal | duration / line / stop
// label | date/time/city/terminal). The stop label only appears for the first
// segment so multi-leg cards don't repeat it.
function drawSegment(
  ctx: Ctx,
  segment: BookingPdfSegment,
  isFirst: boolean,
  stopLabel: string,
  innerW: number,
  airlineLogo: PDFImage | undefined,
) {
  const innerX = ctx.x;

  // Airline logo (when fetched successfully) + flight number + cabin
  const logoSize = 20;
  if (airlineLogo) {
    const scaled = airlineLogo.scaleToFit(logoSize, logoSize);
    ctx.page.drawImage(airlineLogo, {
      x: innerX,
      y: ctx.y - logoSize + 2,
      width: scaled.width,
      height: scaled.height,
    });
  } else {
    drawAirlineBadge(ctx, innerX, ctx.y - 16, 22, 16, segment.airlineCode, C.neutralBadge);
  }
  const textX = innerX + logoSize + 8;
  ctx.page.drawText(segment.airlineName, {
    x: textX,
    y: ctx.y - 11,
    font: ctx.fonts.bold,
    size: 10,
    color: rgbColor(C.ink),
  });
  const nameW = ctx.fonts.bold.widthOfTextAtSize(segment.airlineName, 10);
  const segParts = [`${segment.airlineCode} ${segment.flightNumber}`, segment.cabin].filter(Boolean).join('  ·  ');
  if (segParts) {
    ctx.page.drawText(`  ·  ${segParts}`, {
      x: textX + nameW,
      y: ctx.y - 11,
      font: ctx.fonts.regular,
      size: 10,
      color: rgbColor(C.muted),
    });
  }
  ctx.y -= 22;

  if (segment.operatedBy) {
    ctx.page.drawText(`Operated by ${segment.operatedBy}`, {
      x: innerX,
      y: ctx.y - 9,
      font: ctx.fonts.bold,
      size: 9,
      color: rgbColor(C.ink2),
    });
    ctx.y -= 12;
  }

  // Three-column endpoint block
  const colW = innerW / 3;
  const legTopY = ctx.y - 9;
  const writeLeg = (xStart: number, align: 'left' | 'right', endpoint: BookingPdfSegment['departure']) => {
    const place = (str: string, font: PDFFont, size: number, color: Rgb, y: number) => {
      const measure = font.widthOfTextAtSize(str, size);
      const xx = align === 'right' ? xStart + colW - measure : xStart;
      ctx.page.drawText(str, { x: xx, y, font, size, color: rgbColor(color) });
    };
    place(endpoint.date, ctx.fonts.regular, 9, C.muted, legTopY);
    place(endpoint.time, ctx.fonts.bold, 18, C.ink, legTopY - 21);
    place(endpoint.city, ctx.fonts.regular, 9.5, C.ink2, legTopY - 35);
    if (endpoint.terminal) {
      place(endpoint.terminal, ctx.fonts.regular, 9, C.muted, legTopY - 46);
    }
  };

  writeLeg(innerX, 'left', segment.departure);
  {
    const midX = innerX + colW;
    const midW = colW;
    if (segment.duration) {
      const dw = ctx.fonts.regular.widthOfTextAtSize(segment.duration, 9);
      ctx.page.drawText(segment.duration, {
        x: midX + (midW - dw) / 2,
        y: legTopY - 4,
        font: ctx.fonts.regular,
        size: 9,
        color: rgbColor(C.muted),
      });
    }
    ctx.page.drawLine({
      start: { x: midX + 8, y: legTopY - 16 },
      end: { x: midX + midW - 8, y: legTopY - 16 },
      thickness: 0.6,
      color: rgbColor(C.rule),
    });
    const arrowX = midX + midW - 6;
    const arrowY = legTopY - 16;
    ctx.page.drawLine({
      start: { x: arrowX - 4, y: arrowY + 2.5 },
      end: { x: arrowX, y: arrowY },
      thickness: 0.8,
      color: rgbColor(C.muted2),
    });
    ctx.page.drawLine({
      start: { x: arrowX - 4, y: arrowY - 2.5 },
      end: { x: arrowX, y: arrowY },
      thickness: 0.8,
      color: rgbColor(C.muted2),
    });
    if (isFirst && stopLabel) {
      const dirW = ctx.fonts.regular.widthOfTextAtSize(stopLabel, 8.5);
      ctx.page.drawText(stopLabel, {
        x: midX + (midW - dirW) / 2,
        y: legTopY - 28,
        font: ctx.fonts.regular,
        size: 8.5,
        color: rgbColor(C.muted2),
      });
    }
  }
  writeLeg(innerX + colW * 2, 'right', segment.arrival);

  ctx.y = legTopY - 52;
}

function drawFlight(ctx: Ctx, flight: BookingPdfFlight, airlineLogos: Record<string, PDFImage>) {
  if (flight.note) {
    const lines = wrapText(flight.note, ctx.fonts.regular, 9.5, ctx.contentW);
    for (const line of lines) {
      ctx.page.drawText(line, {
        x: ctx.margin,
        y: ctx.y - 9,
        font: ctx.fonts.regular,
        size: 9.5,
        color: rgbColor(C.muted),
      });
      ctx.y -= 13;
    }
    ctx.y -= 4;
  }

  const endCard = beginCard(ctx, 14);
  const innerX = ctx.x;
  const innerW = ctx.contentW;

  // Title row + right-aligned booking ref
  ctx.page.drawText(flight.title, {
    x: innerX,
    y: ctx.y - 11,
    font: ctx.fonts.bold,
    size: 12,
    color: rgbColor(C.ink),
  });
  if (flight.bookingRef) {
    const bookingLabel = 'Booking  ·  ';
    const labelW = ctx.fonts.regular.widthOfTextAtSize(bookingLabel, 9.5);
    const refW = ctx.fonts.regular.widthOfTextAtSize(flight.bookingRef, 9.5);
    const rightEdge = innerX + innerW;
    ctx.page.drawText(bookingLabel, {
      x: rightEdge - labelW - refW,
      y: ctx.y - 11,
      font: ctx.fonts.regular,
      size: 9.5,
      color: rgbColor(C.muted),
    });
    ctx.page.drawText(flight.bookingRef, {
      x: rightEdge - refW,
      y: ctx.y - 11,
      font: ctx.fonts.regular,
      size: 9.5,
      color: rgbColor(C.ink2),
    });
  }
  ctx.y -= 22;

  // Multi-segment loop with stopover dividers
  flight.segments.forEach((segment, index) => {
    drawSegment(ctx, segment, index === 0, flight.stopLabel, innerW, airlineLogos[segment.airlineCode]);
    if (index < flight.segments.length - 1 && segment.stopoverDuration) {
      const label = 'Stopover: ';
      const lw = ctx.fonts.regular.widthOfTextAtSize(label, 9);
      const vw = ctx.fonts.bold.widthOfTextAtSize(segment.stopoverDuration, 9);
      const totalW = lw + vw;
      const startX = innerX + (innerW - totalW) / 2;
      ctx.y -= 4;
      ctx.page.drawText(label, {
        x: startX,
        y: ctx.y - 9,
        font: ctx.fonts.regular,
        size: 9,
        color: rgbColor(C.muted),
      });
      ctx.page.drawText(segment.stopoverDuration, {
        x: startX + lw,
        y: ctx.y - 9,
        font: ctx.fonts.bold,
        size: 9,
        color: rgbColor(C.ink2),
      });
      ctx.y -= 16;
    }
  });

  // Footer block — baggage + 2-column grid of Seat / Confirmation / e-Ticket
  const footerItems: Array<{ label: string; value: string }> = [];
  if (flight.seatSummary) {
    footerItems.push({ label: 'Seat', value: flight.seatSummary });
  }
  if (flight.airlineConfirmationId) {
    footerItems.push({ label: 'Confirmation ID', value: flight.airlineConfirmationId });
  }
  if (flight.eTicket) {
    footerItems.push({ label: 'e-Ticket', value: flight.eTicket });
  }
  if (flight.baggage || footerItems.length > 0) {
    // Soft rule spans the full card width (edge-to-edge) rather than the
    // inner content width, so it visually attaches to the card border.
    ctx.y -= 5;
    ctx.page.drawLine({
      start: { x: ctx.margin, y: ctx.y },
      end: { x: ctx.pageW - ctx.margin, y: ctx.y },
      thickness: 0.5,
      color: rgbColor(C.ruleSoft),
    });
    ctx.y -= 9;
    if (flight.baggage) {
      const baggageSize = 8;
      const labelText = 'Baggage';
      const labelW = ctx.fonts.regular.widthOfTextAtSize(labelText, baggageSize);
      ctx.page.drawText(labelText, {
        x: innerX,
        y: ctx.y - baggageSize,
        font: ctx.fonts.regular,
        size: baggageSize,
        color: rgbColor(C.muted),
      });
      ctx.page.drawText(`  ·  ${flight.baggage}`, {
        x: innerX + labelW,
        y: ctx.y - baggageSize,
        font: ctx.fonts.regular,
        size: baggageSize,
        color: rgbColor(C.ink2),
      });
      ctx.y -= baggageSize + 4;
    }
    // 2-column row: left item flush-left at innerX, right item flush-right
    // at the card's inner edge so the two columns are pushed as far apart as
    // the card allows.
    for (let i = 0; i < footerItems.length; i += 2) {
      const left = footerItems[i];
      const right = footerItems[i + 1];
      const footerSize = 8;
      const drawAt = (item: { label: string; value: string }, x: number) => {
        const labelText = `${item.label}  ·  `;
        const labelW = ctx.fonts.regular.widthOfTextAtSize(labelText, footerSize);
        ctx.page.drawText(labelText, {
          x,
          y: ctx.y - footerSize,
          font: ctx.fonts.regular,
          size: footerSize,
          color: rgbColor(C.muted),
        });
        ctx.page.drawText(item.value, {
          x: x + labelW,
          y: ctx.y - footerSize,
          font: ctx.fonts.regular,
          size: footerSize,
          color: rgbColor(C.ink2),
        });
      };
      if (left) {
        drawAt(left, innerX);
      }
      if (right) {
        const labelText = `${right.label}  ·  `;
        const labelW = ctx.fonts.regular.widthOfTextAtSize(labelText, footerSize);
        const valueW = ctx.fonts.regular.widthOfTextAtSize(right.value, footerSize);
        drawAt(right, innerX + innerW - labelW - valueW);
      }
      ctx.y -= footerSize + 4;
    }
  }

  endCard();
}

function drawInstructionSections(ctx: Ctx, sections: BookingPdfInstructionSection[], innerX: number, innerW: number) {
  if (sections.length === 0) {
    return;
  }
  sections.forEach((section, idx) => {
    // Keep the title and at least one line together — orphaned titles at the
    // bottom of a page read as a layout bug.
    if (!fits(ctx, 26)) {
      newPage(ctx);
    }
    ctx.y -= 6;
    if (idx === 0) {
      ctx.page.drawLine({
        start: { x: innerX, y: ctx.y + 6 },
        end: { x: innerX + innerW, y: ctx.y + 6 },
        thickness: 0.5,
        color: rgbColor(C.ruleSoft),
      });
    }
    ctx.page.drawText(section.title, {
      x: innerX,
      y: ctx.y - 10,
      font: ctx.fonts.bold,
      size: 10,
      color: rgbColor(C.ink),
    });
    ctx.y -= 14;
    for (const rawLine of section.lines) {
      const wrapped = wrapText(rawLine, ctx.fonts.regular, 9.5, innerW);
      for (const line of wrapped) {
        // Per-line page-break: long supplier text routinely runs off a page.
        if (!fits(ctx, 12)) {
          newPage(ctx);
        }
        ctx.page.drawText(line, {
          x: innerX,
          y: ctx.y - 9,
          font: ctx.fonts.regular,
          size: 9.5,
          color: rgbColor(C.muted),
        });
        ctx.y -= 12;
      }
    }
    if (idx < sections.length - 1) {
      ctx.y -= 6;
      ctx.page.drawLine({
        start: { x: innerX, y: ctx.y },
        end: { x: innerX + innerW, y: ctx.y },
        thickness: 0.5,
        color: rgbColor(C.ruleSoft),
      });
    }
  });
}

// Inset the hero from the card edges so it doesn't dominate the card, and
// round its corners to match the card's own rounding (CARD_CORNER_RADIUS).
// Horizontal inset is generous to keep the photo compact; the top gap is small
// so the card doesn't open with awkward whitespace above the image.
const HERO_INSET_X = 80;
const HERO_INSET_TOP = 14;
const HERO_CORNER_RADIUS = CARD_CORNER_RADIUS;

// Push a rounded-rect clipping path onto the page's graphics stack. Caller is
// responsible for popping the matching `popGraphicsState()` once drawing
// inside the clip is done. We use cubic beziers (kappa ≈ 0.5523) rather than
// drawSvgPath because clipping requires raw path operators.
function pushRoundedRectClip(page: PDFPage, x: number, y: number, w: number, h: number, r: number): void {
  const k = 0.5522847498 * r;
  page.pushOperators(
    pushGraphicsState(),
    moveTo(x + r, y + h),
    lineTo(x + w - r, y + h),
    appendBezierCurve(x + w - r + k, y + h, x + w, y + h - r + k, x + w, y + h - r),
    lineTo(x + w, y + r),
    appendBezierCurve(x + w, y + r - k, x + w - r + k, y, x + w - r, y),
    lineTo(x + r, y),
    appendBezierCurve(x + r - k, y, x, y + r - k, x, y + r),
    lineTo(x, y + h - r),
    appendBezierCurve(x, y + h - r + k, x + r - k, y + h, x + r, y + h),
    clip(),
    endPath(),
  );
}

function drawHeroImage(ctx: Ctx, embedded: PDFImage, innerX: number, innerW: number): void {
  // Match the email's 16:7 aspect ratio — the hero is the most distinctive
  // visual cue that the hotel's been booked, so we keep proportions stable
  // even when the source photo is wider/taller.
  const frameX = innerX + HERO_INSET_X;
  const frameW = innerW - HERO_INSET_X * 2;
  const frameH = (frameW * 7) / 16;
  const frameTop = ctx.y - HERO_INSET_TOP;
  const scaled = embedded.scaleToFit(frameW, frameH);
  const imgX = frameX + (frameW - scaled.width) / 2;
  const imgY = frameTop - frameH + (frameH - scaled.height) / 2;
  pushRoundedRectClip(ctx.page, imgX, imgY, scaled.width, scaled.height, HERO_CORNER_RADIUS);
  ctx.page.drawImage(embedded, {
    x: imgX,
    y: imgY,
    width: scaled.width,
    height: scaled.height,
  });
  ctx.page.pushOperators(popGraphicsState());
  ctx.y = frameTop - frameH;
}

function drawHeroPlaceholder(ctx: Ctx, innerX: number, innerW: number) {
  const frameX = innerX + HERO_INSET_X;
  const frameW = innerW - HERO_INSET_X * 2;
  const frameH = (frameW * 7) / 16;
  const frameTop = ctx.y - HERO_INSET_TOP;
  const frameBottom = frameTop - frameH;
  pushRoundedRectClip(ctx.page, frameX, frameBottom, frameW, frameH, HERO_CORNER_RADIUS);
  ctx.page.drawRectangle({
    x: frameX,
    y: frameBottom,
    width: frameW,
    height: frameH,
    color: rgbColor(C.hero),
  });
  for (let i = -frameH; i < frameW + frameH; i += 14) {
    ctx.page.drawLine({
      start: { x: frameX + i, y: frameTop },
      end: { x: frameX + i + frameH, y: frameBottom },
      thickness: 0.6,
      color: rgb(0.886, 0.91, 0.937),
    });
  }
  ctx.page.pushOperators(popGraphicsState());
  ctx.y = frameBottom;
}

function drawAccommodation(
  ctx: Ctx,
  hotel: BookingPdfAccommodation,
  embedded: PDFImage | undefined,
  opts: { ruleBefore?: boolean } = {},
) {
  // The hero + name/address/dates/kv block draws without internal pagination,
  // so we need to know it'll fit before opening the card. Instructions paginate
  // per-line inside drawInstructionSections, so they can flow across pages and
  // aren't part of this estimate — that's what lets the card start inline
  // rather than getting bumped to a new page by a conservative section() check.
  const innerW = ctx.pageW - ctx.margin * 2;
  const heroRegion = HERO_INSET_TOP + ((innerW - HERO_INSET_X * 2) * 7) / 16 + 14;
  const nonInstructionEst = heroRegion + 80 + 160;
  const { ruleBefore = true } = opts;
  if (!fits(ctx, nonInstructionEst)) {
    newPage(ctx);
  } else if (ruleBefore) {
    drawRule(ctx);
  }

  const endCard = beginCard(ctx, 0); // no inner padding; the card itself contains a flush hero
  const innerX = ctx.margin;

  if (embedded) {
    drawHeroImage(ctx, embedded, innerX, innerW);
  } else {
    drawHeroPlaceholder(ctx, innerX, innerW);
  }

  // From here we want padded content
  const padding = 14;
  const contentX = innerX + padding;
  const contentW = innerW - padding * 2;
  ctx.y -= padding;

  // Header row: hotel name + address (left) — Maps URL would be a link but pdf-lib
  // doesn't render annotations in the visible PDF body, so we surface it as
  // muted text under the address instead.
  ctx.page.drawText(hotel.name, {
    x: contentX,
    y: ctx.y - 11,
    font: ctx.fonts.bold,
    size: 12,
    color: rgbColor(C.ink),
  });
  ctx.y -= 15;
  if (hotel.address) {
    const addressLines = wrapText(hotel.address, ctx.fonts.regular, 9.5, contentW);
    for (const line of addressLines) {
      ctx.page.drawText(line, {
        x: contentX,
        y: ctx.y - 9,
        font: ctx.fonts.regular,
        size: 9.5,
        color: rgbColor(C.muted),
      });
      ctx.y -= 13;
    }
  }
  ctx.y -= 4;
  ctx.page.drawLine({
    start: { x: contentX, y: ctx.y },
    end: { x: contentX + contentW, y: ctx.y },
    thickness: 0.5,
    color: rgbColor(C.ruleSoft),
  });
  ctx.y -= 10;

  // Check-in / Check-out
  const colW = contentW / 2;
  const drawDateCol = (xStart: number, label: string, value: { date: string; sub?: string }) => {
    ctx.page.drawText(label, {
      x: xStart,
      y: ctx.y - 9,
      font: ctx.fonts.regular,
      size: 9,
      color: rgbColor(C.muted),
    });
    ctx.page.drawText(value.date, {
      x: xStart,
      y: ctx.y - 22,
      font: ctx.fonts.bold,
      size: 12,
      color: rgbColor(C.ink),
    });
    if (value.sub) {
      ctx.page.drawText(value.sub, {
        x: xStart,
        y: ctx.y - 33,
        font: ctx.fonts.regular,
        size: 9,
        color: rgbColor(C.muted),
      });
    }
  };
  drawDateCol(contentX, 'Check-in', hotel.checkIn);
  drawDateCol(contentX + colW, 'Check-out', hotel.checkOut);
  ctx.y -= 42;

  ctx.page.drawLine({
    start: { x: contentX, y: ctx.y },
    end: { x: contentX + contentW, y: ctx.y },
    thickness: 0.5,
    color: rgbColor(C.ruleSoft),
  });
  ctx.y -= 8;

  // KV detail rows
  const kvRow = (label: string, value: string) => {
    drawKV(ctx, label, value, { x: contentX, labelWidth: 110 });
  };
  if (hotel.room) {
    kvRow('Room', hotel.room);
  }
  if (hotel.bed) {
    const bedText = hotel.bedNotGuaranteed ? `${hotel.bed} (Not guaranteed)` : hotel.bed;
    kvRow('Bed', bedText);
  }
  if (hotel.guests) {
    kvRow('Guests', hotel.guests);
  }
  if (hotel.included) {
    kvRow('Included', hotel.included);
  }
  if (hotel.bookingId) {
    kvRow('Booking ID', hotel.bookingId);
  }
  if (hotel.cancellation) {
    const size = 10;
    const labelX = contentX;
    const valueX = contentX + 110;
    ctx.page.drawText('Cancellation', {
      x: labelX,
      y: ctx.y - size,
      font: ctx.fonts.regular,
      size,
      color: rgbColor(C.muted),
    });
    const wrapped = wrapText(hotel.cancellation.text, ctx.fonts.regular, size, contentW - 110);
    wrapped.forEach((line, idx) => {
      ctx.page.drawText(line, {
        x: valueX,
        y: ctx.y - size,
        font: idx === 0 ? ctx.fonts.bold : ctx.fonts.regular,
        size,
        color: rgbColor(hotel.cancellation!.refundable ? C.good : C.bad),
      });
      ctx.y -= 14;
    });
  }

  if (hotel.instructionSections.length > 0) {
    ctx.y -= 6;
    drawInstructionSections(ctx, hotel.instructionSections, contentX, contentW);
  }

  ctx.y -= padding;
  endCard();
}

function drawPrice(ctx: Ctx, price: BookingPdfPrice) {
  drawSectionTitle(ctx, 'Price summary');
  ctx.y -= 8;
  const right = ctx.margin + ctx.contentW;

  const drawPriceLine = (line: BookingPdfPriceLine) => {
    const size = line.sub ? 9 : 10.5;
    const font = ctx.fonts.regular;
    const color: Rgb = line.sub ? C.muted : C.ink2;
    ctx.page.drawText(line.label, {
      x: ctx.margin,
      y: ctx.y - size,
      font,
      size,
      color: rgbColor(color),
    });
    const valW = font.widthOfTextAtSize(line.value, size);
    ctx.page.drawText(line.value, {
      x: right - valW,
      y: ctx.y - size,
      font,
      size,
      color: rgbColor(color),
    });
    ctx.y -= line.sub ? 16 : 18;
  };

  for (const line of price.lines) {
    if (!fits(ctx, line.sub ? 16 : 18)) {
      newPage(ctx);
    }
    drawPriceLine(line);
  }

  // Keep summary + total + charge together (rule + optional summary + total +
  // charge ≈ 86px) so they never get orphaned across a page boundary.
  if (!fits(ctx, price.summary ? 86 : 70)) {
    newPage(ctx);
  }
  drawRule(ctx, 20);

  if (price.summary) {
    drawPriceLine(price.summary);
  }

  const totalSize = 13;
  ctx.page.drawText(price.total.label, {
    x: ctx.margin,
    y: ctx.y - totalSize,
    font: ctx.fonts.bold,
    size: totalSize,
    color: rgbColor(C.ink),
  });
  const totW = ctx.fonts.bold.widthOfTextAtSize(price.total.value, totalSize);
  ctx.page.drawText(price.total.value, {
    x: right - totW,
    y: ctx.y - totalSize,
    font: ctx.fonts.bold,
    size: totalSize,
    color: rgbColor(C.ink),
  });
  ctx.y -= 26;

  if (price.charge) {
    ctx.page.drawText(price.charge.description, {
      x: ctx.margin,
      y: ctx.y - 9,
      font: ctx.fonts.regular,
      size: 9,
      color: rgbColor(C.muted),
    });
    if (price.charge.date) {
      const cdW = ctx.fonts.regular.widthOfTextAtSize(price.charge.date, 9);
      ctx.page.drawText(price.charge.date, {
        x: right - cdW,
        y: ctx.y - 9,
        font: ctx.fonts.regular,
        size: 9,
        color: rgbColor(C.muted),
      });
    }
    ctx.y -= 22;
  }

  if (price.disclaimer) {
    const lines = wrapText(price.disclaimer, ctx.fonts.regular, 8.5, ctx.contentW);
    for (const ln of lines) {
      // Per-line page break — the disclaimer was the segment running into the
      // footer because the section() estimate underestimates it.
      if (!fits(ctx, 13)) {
        newPage(ctx);
      }
      ctx.page.drawText(ln, {
        x: ctx.margin,
        y: ctx.y - 8,
        font: ctx.fonts.regular,
        size: 8.5,
        color: rgbColor(C.muted2),
      });
      ctx.y -= 13;
    }
  }
}

// Append a clickable URI link annotation over the given rectangle. Annotations
// live on the page's /Annots array; we add to it when present, or create it.
function addLinkAnnotation(page: PDFPage, rect: { x: number; y: number; width: number; height: number }, uri: string) {
  const linkAnnotation = page.doc.context.obj({
    Type: 'Annot',
    Subtype: 'Link',
    Rect: [rect.x, rect.y, rect.x + rect.width, rect.y + rect.height],
    Border: [0, 0, 0],
    A: {
      Type: 'Action',
      S: 'URI',
      URI: PDFString.of(uri),
    },
  });
  const existing = page.node.lookup(PDFName.of('Annots'), PDFArray);
  if (existing) {
    existing.push(linkAnnotation);
  } else {
    page.node.set(PDFName.of('Annots'), page.doc.context.obj([linkAnnotation]));
  }
}

function drawFooterOnPage(page: PDFPage, fonts: Fonts, pageNum: number, totalPages: number) {
  const m = 48;
  const w = page.getWidth();
  const footerY = m - 4;
  page.drawLine({
    start: { x: m, y: footerY + 22 },
    end: { x: w - m, y: footerY + 22 },
    thickness: 0.6,
    color: rgbColor(C.rule),
  });
  // Draw the line as a single string for consistent layout, but measure each
  // segment so we can drop a clickable annotation precisely over the email and
  // phone runs without changing their visual style.
  const size = 8.5;
  const brandSegment = 'Altitude AI  ·  ';
  const email = 'support@getaltitude.ai';
  const separator = '  ·  ';
  const phone = '(+61) 0495 007 246';
  page.drawText(`${brandSegment}${email}${separator}${phone}`, {
    x: m,
    y: footerY + 8,
    font: fonts.regular,
    size,
    color: rgbColor(C.muted2),
  });

  const brandW = fonts.regular.widthOfTextAtSize(brandSegment, size);
  const emailW = fonts.regular.widthOfTextAtSize(email, size);
  const sepW = fonts.regular.widthOfTextAtSize(separator, size);
  const phoneW = fonts.regular.widthOfTextAtSize(phone, size);
  const linkHeight = size + 2;
  addLinkAnnotation(page, { x: m + brandW, y: footerY + 6, width: emailW, height: linkHeight }, `mailto:${email}`);
  addLinkAnnotation(
    page,
    { x: m + brandW + emailW + sepW, y: footerY + 6, width: phoneW, height: linkHeight },
    `tel:+61495007246`,
  );

  const right = `${pageNum} / ${totalPages}`;
  const rw = fonts.regular.widthOfTextAtSize(right, size);
  page.drawText(right, {
    x: w - m - rw,
    y: footerY + 8,
    font: fonts.regular,
    size,
    color: rgbColor(C.muted2),
  });
}

// ---- Pagination ----------------------------------------------------------
const FOOTER_RESERVE = 36;

function newPage(ctx: Ctx) {
  // If a card is open, close out its current-page portion (sides + top when
  // this is the originating page, sides only on continuation pages) so the
  // border carries visually onto the next page.
  if (ctx.card) {
    const bottom = ctx.margin + FOOTER_RESERVE;
    drawCardEdges(
      ctx.card.page,
      { x: ctx.margin, y: bottom, width: ctx.pageW - ctx.margin * 2, height: ctx.card.topY - bottom },
      { top: !ctx.card.isContinuation, bottom: false },
    );
  }
  const page = ctx.doc.addPage([ctx.pageW, ctx.pageH]);
  patchPageDrawText(page);
  ctx.page = page;
  ctx.y = ctx.pageH - ctx.margin;
  ctx.x = ctx.margin;
  ctx.contentW = ctx.pageW - ctx.margin * 2;
  // Re-anchor the open card to the top of the new page so the closer (or the
  // next newPage call) draws the continuation portion correctly.
  if (ctx.card) {
    ctx.card = { topY: ctx.y, page: ctx.page, isContinuation: true };
  }
}

function fits(ctx: Ctx, height: number): boolean {
  return ctx.y - height >= ctx.margin + FOOTER_RESERVE;
}

function section(ctx: Ctx, estimatedHeight: number, draw: (c: Ctx) => void, opts: { ruleBefore?: boolean } = {}) {
  const { ruleBefore = true } = opts;
  if (!fits(ctx, estimatedHeight)) {
    newPage(ctx);
  } else if (ruleBefore) {
    drawRule(ctx);
  }
  draw(ctx);
}

// ---- Main entry ----------------------------------------------------------

// Render a booking confirmation PDF and return the raw bytes. Caller is
// responsible for wrapping the bytes in a Blob / download flow.
export async function renderBookingPdf(data: BookingPdfData): Promise<Uint8Array> {
  const doc = await PDFDocument.create();
  doc.setTitle(`Booking ${data.bookingId ?? ''} — ${data.title}`);
  doc.setAuthor('Altitude Travel');
  doc.setSubject('Booking confirmation');
  doc.setProducer('pdf-lib');
  doc.setCreator('Altitude PDF generator');

  const page = doc.addPage([595.276, 841.89]); // A4
  patchPageDrawText(page);

  const fonts: Fonts = {
    regular: await doc.embedFont(StandardFonts.Helvetica),
    bold: await doc.embedFont(StandardFonts.HelveticaBold),
  };
  patchFontWidth(fonts.regular);
  patchFontWidth(fonts.bold);

  // Embed hotel hero images + airline logos up front so the draw loop stays
  // synchronous. Both source datasets came over the wire already (pre-fetched
  // by the adapter), so this is purely the pdf-lib embed step.
  const [heroImages, airlineLogoEntries] = await Promise.all([
    Promise.all(
      data.accommodations.map(async (hotel): Promise<PDFImage | undefined> => {
        if (!hotel.heroImage) {
          return undefined;
        }
        return hotel.heroImage.ext === 'png'
          ? doc.embedPng(hotel.heroImage.bytes)
          : doc.embedJpg(hotel.heroImage.bytes);
      }),
    ),
    Promise.all(
      Object.entries(data.airlineLogos).map(async ([code, logo]) => {
        const embedded = logo.ext === 'png' ? await doc.embedPng(logo.bytes) : await doc.embedJpg(logo.bytes);
        return [code, embedded] as const;
      }),
    ),
  ]);
  const airlineLogos: Record<string, PDFImage> = Object.fromEntries(airlineLogoEntries);

  const ctx = makeCtx(doc, page, fonts);

  drawHeader(ctx, data);

  section(ctx, 60, (c) => drawTraveler(c, data));

  for (const flight of data.flights) {
    const segmentCount = flight.segments.length;
    const estHeight = 80 + segmentCount * 90 + (segmentCount - 1) * 20 + 40;
    section(ctx, estHeight, (c) => drawFlight(c, flight, airlineLogos));
  }

  // Accommodation cards manage their own pre-flight + pagination — instructions
  // can flow across pages inside drawInstructionSections, so we don't gate the
  // whole card on a worst-case estimate the way section() would.
  data.accommodations.forEach((hotel, idx) => {
    drawAccommodation(ctx, hotel, heroImages[idx]);
  });

  if (data.manualBookingNotice) {
    section(ctx, 80, (c) => {
      drawSectionTitle(c, 'Cancellation policy');
      const lines = wrapText(data.manualBookingNotice as string, c.fonts.regular, 9.5, c.contentW);
      for (const line of lines) {
        c.page.drawText(line, {
          x: c.margin,
          y: c.y - 9,
          font: c.fonts.regular,
          size: 9.5,
          color: rgbColor(C.muted),
        });
        c.y -= 13;
      }
    });
  }

  if (data.price) {
    const lineCount = data.price.lines.length;
    section(ctx, 110 + lineCount * 14, (c) => drawPrice(c, data.price as BookingPdfPrice));
  }

  const pages = doc.getPages();
  pages.forEach((p, i) => {
    drawFooterOnPage(p, fonts, i + 1, pages.length);
  });

  return doc.save();
}
